import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { GET_STORE_AGE_VALIDATOR_CONFIG, UPDATE_STORE_AGE_VALIDATOR_CONFIG } from './graphql';

type VersionCondition = { op: string; value: string };
type StoreAgeValidatorDraft = {
  enabled: boolean;
  platform: string;
  versionConditions: VersionCondition[];
  countriesText: string;
  kidModeNoticeEnabled: boolean;
  noticeTextsJson: string;
};

const PLATFORM_OPTIONS = [
  { value: 'ALL', label: 'Both platforms' },
  { value: 'IOS', label: 'iOS only' },
  { value: 'ANDROID', label: 'Android only' },
];

const VERSION_OPS = ['>=', '>', '<=', '<', '==', '!='];
const VERSION_REGEX = /^\d+(\.\d+)*$/;

// Matches the game's built-in fallback text.
const DEFAULT_NOTICE_TEXT_EN =
  'Based on age information from your app store, your account will use the kids experience.';

const formatTextsJson = (value: Record<string, string> | null | undefined) =>
  JSON.stringify(value && Object.keys(value).length ? value : { en: DEFAULT_NOTICE_TEXT_EN }, null, 2);

const parseNoticeTexts = (value: string): Record<string, string> => {
  let parsedValue: unknown;

  try {
    parsedValue = JSON.parse(value);
  } catch {
    throw new Error('Notice texts must be valid JSON.');
  }

  if (!parsedValue || typeof parsedValue !== 'object' || Array.isArray(parsedValue)) {
    throw new Error('Notice texts must be a JSON object like {"en":"..."}.');
  }

  const texts: Record<string, string> = {};
  for (const [locale, text] of Object.entries(parsedValue)) {
    if (typeof text !== 'string') {
      throw new Error(`Notice text for "${locale}" must be a string.`);
    }
    const trimmedLocale = locale.trim().toLowerCase();
    const trimmedText = text.trim();
    if (trimmedLocale && trimmedText) {
      texts[trimmedLocale] = trimmedText;
    }
  }

  if (Object.keys(texts).length > 0 && !texts.en) {
    throw new Error('Add an "en" text - it is the fallback for all other languages.');
  }

  return texts;
};

export default function StoreAgeValidatorCard() {
  const { data, loading, error, refetch } = useQuery(GET_STORE_AGE_VALIDATOR_CONFIG, {
    fetchPolicy: 'network-only',
  });
  const [updateConfig, { loading: saving }] = useMutation(UPDATE_STORE_AGE_VALIDATOR_CONFIG);

  const [draft, setDraft] = useState<StoreAgeValidatorDraft | null>(null);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'danger'; message: string } | null>(null);

  const saved = data?.liveOps?.storeAgeValidatorConfig;

  useEffect(() => {
    if (saved && !draft) {
      setDraft({
        enabled: saved.enabled,
        platform: saved.platform,
        versionConditions: saved.versionConditions.map(({ op, value }: VersionCondition) => ({ op, value })),
        countriesText: (saved.countries ?? []).join(', '),
        kidModeNoticeEnabled: saved.kidModeNoticeEnabled,
        noticeTextsJson: formatTextsJson(saved.kidModeNoticeTexts),
      });
    }
  }, [saved, draft]);

  const setCondition = (index: number, patch: Partial<VersionCondition>) => {
    setDraft(previous =>
      previous
        ? {
            ...previous,
            versionConditions: previous.versionConditions.map((condition, i) =>
              i === index ? { ...condition, ...patch } : condition
            ),
          }
        : previous
    );
  };

  const addCondition = () => {
    setDraft(previous =>
      previous
        ? { ...previous, versionConditions: [...previous.versionConditions, { op: '>=', value: '' }] }
        : previous
    );
  };

  const removeCondition = (index: number) => {
    setDraft(previous =>
      previous
        ? { ...previous, versionConditions: previous.versionConditions.filter((_, i) => i !== index) }
        : previous
    );
  };

  const save = async () => {
    if (!draft) {
      return;
    }
    setFeedback(null);
    const invalid = draft.versionConditions.find(condition => !VERSION_REGEX.test(condition.value.trim()));
    if (invalid) {
      setFeedback({
        type: 'danger',
        message: `Version "${invalid.value}" is not valid - use digits and dots, e.g. 003.038.000.`,
      });
      return;
    }
    const countries = draft.countriesText
      .split(/[,\s]+/)
      .map(code => code.trim().toUpperCase())
      .filter(Boolean);
    const badCountry = countries.find(code => !/^[A-Z]{2}$/.test(code));
    if (badCountry) {
      setFeedback({ type: 'danger', message: `"${badCountry}" is not a 2-letter ISO country code.` });
      return;
    }
    let kidModeNoticeTexts: Record<string, string>;
    try {
      kidModeNoticeTexts = parseNoticeTexts(draft.noticeTextsJson);
    } catch (parseError) {
      setFeedback({ type: 'danger', message: (parseError as Error).message });
      return;
    }
    try {
      await updateConfig({
        variables: {
          input: {
            enabled: draft.enabled,
            platform: draft.platform,
            versionConditions: draft.versionConditions.map(condition => ({
              op: condition.op,
              value: condition.value.trim(),
            })),
            countries,
            kidModeNoticeEnabled: draft.kidModeNoticeEnabled,
            kidModeNoticeTexts,
          },
        },
      });
      await refetch();
      setFeedback({ type: 'success', message: 'Saved. Clients pick it up on their next legal setup fetch (app launch).' });
    } catch (mutationError) {
      setFeedback({ type: 'danger', message: (mutationError as Error).message });
    }
  };

  if (loading && !data) {
    return <Spinner animation="border" className="m-4" />;
  }

  return (
    <Card className="mb-4">
      <Card.Header>
        Store Age Validator (Legal SDK){' '}
        {saved &&
          (saved.enabled ? <Badge variant="success">Enabled</Badge> : <Badge variant="secondary">Disabled</Badge>)}
      </Card.Header>
      <Card.Body>
        <Alert variant="info" className="small">
          Store age check (Google Age Signals / Apple Declared Age Range) at age-gate submission. All matchers must
          pass, otherwise the typed age is used alone. Applies on next app launch, no deploy.
        </Alert>
        {error && <Alert variant="danger">{error.message}</Alert>}
        {feedback && (
          <Alert variant={feedback.type} onClose={() => setFeedback(null)} dismissible>
            {feedback.message}
          </Alert>
        )}
        {draft && (
          <>
            <Row className="align-items-center mb-3">
              <Col xs="auto">
                <Form.Check
                  type="switch"
                  id="store-age-validator-enabled"
                  label={draft.enabled ? 'Enabled' : 'Disabled'}
                  checked={draft.enabled}
                  onChange={() =>
                    setDraft(previous => (previous ? { ...previous, enabled: !previous.enabled } : previous))
                  }
                />
              </Col>
              <Col xs="auto">
                <Form.Control
                  as="select"
                  size="sm"
                  value={draft.platform}
                  onChange={event => {
                    const platform = event.target.value;
                    setDraft(previous => (previous ? { ...previous, platform } : previous));
                  }}
                >
                  {PLATFORM_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Control>
              </Col>
            </Row>

            <Form.Group className="mb-3" style={{ maxWidth: 420 }}>
              <Form.Label className="small">
                <b>Countries</b> - ISO-2 codes, comma-separated (e.g. <code>BR, US</code>).{' '}
                {draft.countriesText.trim() ? (
                  <Badge variant="primary">Only: {draft.countriesText}</Badge>
                ) : (
                  <Badge variant="warning">Empty = ALL countries</Badge>
                )}
              </Form.Label>
              <Form.Control
                size="sm"
                value={draft.countriesText}
                onChange={event => {
                  const countriesText = event.target.value;
                  setDraft(previous => (previous ? { ...previous, countriesText } : previous));
                }}
              />
            </Form.Group>

            <Row className="align-items-center mb-3">
              <Col xs="auto">
                <Form.Check
                  type="switch"
                  id="store-age-kid-mode-notice-enabled"
                  label="Show notice popup when the store downgrades a typed adult age"
                  checked={draft.kidModeNoticeEnabled}
                  onChange={() =>
                    setDraft(previous =>
                      previous ? { ...previous, kidModeNoticeEnabled: !previous.kidModeNoticeEnabled } : previous
                    )
                  }
                />
              </Col>
            </Row>

            <Form.Group className="mb-3" style={{ maxWidth: 640 }}>
              <Form.Label className="small">
                <b>Notice popup texts</b> - JSON, locale → text, fallback <code>en</code>. <code>{'{}'}</code> =
                built-in message.
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                style={{ fontFamily: 'monospace', fontSize: 12 }}
                value={draft.noticeTextsJson}
                onChange={event => {
                  const noticeTextsJson = event.target.value;
                  setDraft(previous => (previous ? { ...previous, noticeTextsJson } : previous));
                }}
              />
            </Form.Group>

            <b className="small">App version conditions (all must match)</b>
            <Table size="sm" borderless style={{ maxWidth: 420 }}>
              <tbody>
                {draft.versionConditions.map((condition, index) => (
                  <tr key={index}>
                    <td style={{ width: 90 }}>
                      <Form.Control
                        as="select"
                        size="sm"
                        value={condition.op}
                        onChange={event => setCondition(index, { op: event.target.value })}
                      >
                        {VERSION_OPS.map(op => (
                          <option key={op} value={op}>
                            {op}
                          </option>
                        ))}
                      </Form.Control>
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        placeholder="003.038.000"
                        value={condition.value}
                        onChange={event => setCondition(index, { value: event.target.value })}
                      />
                    </td>
                    <td style={{ width: 40 }}>
                      <Button size="sm" variant="outline-danger" onClick={() => removeCondition(index)}>
                        ×
                      </Button>
                    </td>
                  </tr>
                ))}
                {draft.versionConditions.length === 0 && (
                  <tr>
                    <td colSpan={3} className="text-muted small">
                      No conditions - all versions.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
            <Button size="sm" variant="outline-secondary" onClick={addCondition}>
              Add version condition
            </Button>

            <hr />
            <Button variant="success" disabled={saving} onClick={save}>
              {saving ? 'Saving...' : 'Save all settings'}
            </Button>
          </>
        )}
      </Card.Body>
    </Card>
  );
}
